import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { RemirrorJSON } from 'remirror';
import { ProjectFragment } from 'components/fragments.generated';
import { StudioClipPreviewWrapperProps } from '../StudioClipPreview/StudioClipPreviewWrapper';
import {
  ClipPreview,
  useStudioClipPreviewListContext,
} from '../../context/previewClip/StudioPreviewClipContext';
import { StudioClipListProps } from '../StudioClipList';
import {
  ClipObject,
  ClipObjectPending,
  instanceOfCompletedClip,
} from '../../context/clips';
import useStudioClipCompleteStyles from '../StudioClip/States/Complete/completeStyles';
import StudioClipWithUserActions from '../StudioClipUserList/StudioClipWithUserActions';
import { Can } from '../../context/projectAccess/ProjectAccessContext';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../server/abilities/ProjectAccessEnums';
import useClipCreator from '../../context/clips/useClipCreator';
import StudioClipPreviewList from '../StudioClipPreviewList';
import StudioClipWrapper from '../StudioClip/StudioClipWrapper';
import useSavePreviewClip from '../StudioClipPreview/hooks/useSavePreviewClip';
import Editor from '../../context/editor';
import { repopulate } from '../../store/EditorSlice';
import { getCoreEditorRef } from '../../context/editorV2/CoreEditorContext';
import { useAppDispatch } from '../../store/hooks';

export interface StudioClipPreviewListWrapperProps
  extends Omit<
    StudioClipPreviewWrapperProps,
    'avatarVariantStyle' | 'projectId'
  > {
  addClipToPreviews: (
    clipId: string,
    clip: ClipObject | ClipObjectPending,
    json: RemirrorJSON
  ) => void;
  deleteClipFromPreviews: (clip: ClipObject) => Promise<void>;
  previewClip?: ReturnType<typeof useClipCreator>['4'];
  previewClipError?: ReturnType<typeof useClipCreator>['1']['error'];
  currentClipId?: ReturnType<typeof useClipCreator>['1']['currentClipId'];
  resetPreviewClip: () => void;
  project: ProjectFragment;
}

const StudioClipPreviewListWrapper = ({
  actor,
  addClipToPreviews,
  currentClipId,
  deleteClipFromPreviews,
  previewClip,
  previewClipError,
  resetPreviewClip,
  project,
}: StudioClipPreviewListWrapperProps) => {
  const { previews, setPreviews, setPreviewToSaved } =
    useStudioClipPreviewListContext();
  const [pendingClipId, setPendingClipId] =
    useState<StudioClipPreviewListWrapperProps['currentClipId']>();
  const [error, setError] = useState<string | undefined>();
  const coreEditor = getCoreEditorRef();

  useEffect(() => {
    if (previewClipError) {
      setError(previewClipError);
    }
  }, [previewClipError]);

  useEffect(() => {
    /**
     * If previewClip.id changes, that means we have a new preview.
     * Add to preview list, then reset previewClip to allow for another
     */
    if (previewClip && previewClip.isComplete && pendingClipId) {
      const currentPreview = previews?.get(previewClip.id);
      if (!currentPreview) {
        const json = coreEditor?.helpers.getJSON();

        if (json) {
          addClipToPreviews(pendingClipId, previewClip, json);
          setPendingClipId(undefined);
          resetPreviewClip();
        }
      }
    }
  }, [
    previewClip?.id,
    previewClip?.isComplete,
    currentClipId,
    previews,
    previewClip,
    pendingClipId,
    addClipToPreviews,
    resetPreviewClip,
    coreEditor,
  ]);

  useEffect(() => {
    if (currentClipId) {
      setPendingClipId(currentClipId);
    }
  }, [currentClipId]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      if (result.source.index === result.destination.index) {
        return;
      }

      const newMap = new Map();
      const previewArr = previews
        ? Array.from(previews).map(([clipId, clip], _index) => ({
            ...clip,
            id: clipId,
          }))
        : [];

      // Remove the item from the sourceIndex and store it
      const [removedItem] = previewArr.splice(result.source.index, 1);

      // Insert the removed item into the destinationIndex
      previewArr.splice(result.destination.index, 0, removedItem);

      // Use correctly ordered array to set items in new map
      previewArr.forEach(clip => {
        newMap.set(clip.id, clip);
      });

      setPreviews(newMap);
    },
    [previews, setPreviews]
  );

  const completeClasses = useStudioClipCompleteStyles();

  const [saveClip] = useSavePreviewClip();
  const dispatch = useAppDispatch();

  const handleReplaceEditorValue = useCallback(
    (newValue: RemirrorJSON) => {
      const json = newValue as RemirrorJSON;
      dispatch(repopulate({ json }));
    },
    [dispatch]
  );

  const handleSave = useCallback(
    async ({
      clip,
      editorText,
      setIsClipSaved,
    }: {
      clip: ClipObject;
      editorText: string;
      setIsClipSaved: (isSaved: boolean) => void;
    }) => {
      const defaultRenderMode: Editor.StoreState['renderMode'] = 'all';

      if (clip && clip.fileName && editorText && clip.textFormat) {
        const inputs = {
          clipId: clip.id,
          projectId: clip.projectId,
          actor: clip.actor,
          textInput: clip.text,
          textFormat: clip.textFormat,
          renderMode: defaultRenderMode,
          ttsVersion: actor.ttsVersion ?? undefined,
          fileName: clip.fileName,
        };
        await saveClip(inputs);

        setPreviewToSaved(clip.id);
        setIsClipSaved(true);
      }
    },
    [actor.ttsVersion, saveClip, setPreviewToSaved]
  );

  const handleRenderDroppableClone = useCallback(
    (
      provided: DraggableProvided,
      snapshot: DraggableStateSnapshot,
      rubric: DraggableRubric
    ) => {
      const previewArr =
        previews &&
        Array.from(previews).map(([clipId, clip], index) => ({
          ...clip,
          id: clipId,
          index,
        }));
      const clip = previewArr && previewArr[rubric.source.index];
      if (!clip) return <div>{`Clip not found!`}</div>;
      return (
        <StudioClipWrapper draggableProvided={provided}>
          <StudioClipWithUserActions
            clip={clip as ClipObject}
            previewClip={clip as ClipPreview}
            onDelete={deleteClipFromPreviews}
            selected={false}
            onSelectedChange={undefined}
            studioClipCompleteClasses={completeClasses}
            draggableProvided={provided}
            onReplaceEditorValue={handleReplaceEditorValue}
            isDragging={snapshot.isDragging}
            projectId={project.id}
            hideClipBodyTop
            handleSave={handleSave}
            project={project}
          />
        </StudioClipWrapper>
      );
    },
    [
      completeClasses,
      deleteClipFromPreviews,
      handleReplaceEditorValue,
      handleSave,
      previews,
      project.id,
    ]
  );

  const clipRenderer = useMemo<StudioClipListProps['clipRenderer']>(
    () =>
      // eslint-disable-next-line react/no-unstable-nested-components
      ({ clip, index }) => {
        // Only include the Draggable component for completed clips
        const isComplete = instanceOfCompletedClip(clip);
        const studioClipProps = {
          clip: clip as ClipObject,
          previewClip: clip as ClipPreview,
          handleSave,
          onDelete: deleteClipFromPreviews,
          onReplaceEditorValue: handleReplaceEditorValue,
          selected: false,
          onSelectedChange: undefined,
          studioClipCompleteClasses: completeClasses,
          projectId: project.id,
          hideClipBodyTop: true,
          hideBorder: true,
          previewError: error,
        };

        return isComplete ? (
          <Can
            I={ProjectAccessAction.Move}
            a={ProjectAccessSubject.Clip}
            passThrough
          >
            {(allowed: boolean) => (
              <Draggable
                key={clip.id}
                draggableId={clip.id}
                index={index}
                isDragDisabled={!allowed}
              >
                {(
                  provided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => (
                  <StudioClipWrapper draggableProvided={provided}>
                    <StudioClipWithUserActions
                      {...studioClipProps}
                      draggableProvided={allowed ? provided : undefined}
                      isDragging={snapshot.isDragging}
                      project={project}
                    />
                  </StudioClipWrapper>
                )}
              </Draggable>
            )}
          </Can>
        ) : (
          <StudioClipWithUserActions {...studioClipProps} project={project} />
        );
      },
    [
      completeClasses,
      deleteClipFromPreviews,
      error,
      handleReplaceEditorValue,
      handleSave,
      project.id,
    ]
  );

  return previews ? (
    <StudioClipPreviewList
      previews={previews}
      onDragEnd={onDragEnd}
      clipRenderer={clipRenderer}
      handleRenderDroppableClone={handleRenderDroppableClone}
    />
  ) : null;
};

export default StudioClipPreviewListWrapper;
